import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { sealingTipById } from "@/lib/sealingTips";
import { CheckCircle2, Circle, X } from "lucide-react";
import { t, LANG } from "@/lib/i18n";

const ActionPlan = ({ items, actionLevel }) => {
  const [completed, setCompleted] = useState([]);
  const [tips, setTips] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/action-plan");
        if (alive) {
          setCompleted(data.completed_items || []);
          setTips(data.sealing_tips || []);
          setLoaded(true);
        }
      } catch {
        setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  const toggle = async (id) => {
    const next = completed.includes(id)
      ? completed.filter((x) => x !== id)
      : [...completed, id];
    setCompleted(next);
    try {
      await api.put("/action-plan", { completed_items: next });
    } catch {
      // ignore — will re-sync on refresh
    }
  };

  const removeTip = async (id) => {
    setTips((t) => t.filter((x) => x !== id));
    setCompleted((c) => c.filter((x) => x !== id));
    try {
      await api.delete(`/action-plan/sealing-tips/${id}`);
    } catch {
      // ignore — will re-sync on refresh
    }
  };

  const tipItems = tips.map(sealingTipById).filter(Boolean)
    .map((tp) => ({ id: tp.id, title: `${t("Seal:")} ${tp[LANG].title}`, description: tp[LANG].tip, removable: true }));
  const allItems = [...(items || []), ...tipItems];
  if (allItems.length === 0) return null;

  const doneCount = allItems.filter((i) => completed.includes(i.id)).length;

  return (
    <div className="card-flat" data-testid={TESTIDS.actionPlan}>
      <div className="flex items-baseline justify-between gap-4 mb-4">
        <div>
          <p className="eyebrow">{t("Your personalized action plan")}</p>
          <h3 className="font-serif text-xl mt-1 text-[#1C201F]">
            {t("What you should do next")}
          </h3>
        </div>
        {loaded && (
          <span className="text-xs text-[#5C6661] whitespace-nowrap">
            {doneCount} / {allItems.length} {t("complete")}
          </span>
        )}
      </div>
      <ul className="space-y-3">
        {allItems.map((it) => {
          const isDone = completed.includes(it.id);
          return (
            <li
              key={it.id}
              data-testid={TESTIDS.actionPlanItem}
              data-item-id={it.id}
              data-completed={isDone}
              className="flex items-start gap-1"
            >
              <button
                type="button"
                onClick={() => toggle(it.id)}
                className="flex-1 flex items-start gap-3 text-left p-3 rounded-xl hover:bg-[#F4F7F6] transition-colors"
              >
                <span className="mt-0.5">
                  {isDone ? (
                    <CheckCircle2 size={20} className="text-[#2F524A]" />
                  ) : (
                    <Circle size={20} className="text-[#C8CFCB]" />
                  )}
                </span>
                <span>
                  <span
                    className={`block text-sm font-semibold ${
                      isDone ? "text-[#5C6661] line-through" : "text-[#1C201F]"
                    }`}
                  >
                    {t(it.title)}
                  </span>
                  <span className="block text-xs text-[#5C6661] mt-0.5 leading-relaxed">
                    {t(it.description)}
                  </span>
                </span>
              </button>
              {it.removable && (
                <button type="button" onClick={() => removeTip(it.id)} aria-label={`${t("Remove")} ${t(it.title)}`}
                  className="mt-3 p-1.5 rounded-full text-[#5C6661] hover:bg-[#F4F7F6] hover:text-[#1C201F] transition-colors"
                  data-testid={`action-plan-remove-${it.id}`}>
                  <X size={14} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {actionLevel === "action" || actionLevel === "high" || actionLevel === "very-high" ? (
        <div
          className="mt-6 p-4 rounded-xl border border-[#E8ECEA] bg-[#F4F7F6]"
          data-testid={TESTIDS.cr3Callout}
        >
          <p className="eyebrow text-[#2F524A]">{t("Need financial assistance?")}</p>
          <p className="text-sm text-[#1C201F] mt-1 leading-snug">
            {t("You may qualify for a Radon Community Mitigation Installation Project.")}
          </p>
          <Link to="/assistance" className="text-xs font-semibold text-[#2F524A] hover:underline mt-2 inline-block"
            data-testid="action-plan-assistance-link">
            {t("Start the assistance screening →")}
          </Link>
        </div>
      ) : null}
    </div>
  );
};

export default ActionPlan;
